import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
} from '@apollo/client';
import { ApolloProvider } from '@apollo/client/react';
import { SetContextLink } from '@apollo/client/link/context';
import { RetryLink } from '@apollo/client/link/retry';
import { useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { graphqlApiUrl } from '../config';
import { useE2nApiAuth } from './E2nApiAuthContext';

function buildClient(getAccessToken: () => Promise<string | undefined>) {
  const httpLink = new HttpLink({ uri: graphqlApiUrl });

  const authLink = new SetContextLink(async ({ headers }) => {
    let accessToken: string | undefined;
    try {
      accessToken = await getAccessToken();
    } catch {
      // no token available, request continues unauthenticated
    }

    return {
      headers: {
        ...headers,
        authorization: accessToken ? `Bearer ${accessToken}` : '',
      },
    };
  });

  const retryLink = new RetryLink({
    delay: {
      initial: 100,
      max: 1000,
      jitter: false,
    },
    attempts: {
      max: 3,
      retryIf: (error, _operation) => {
        return !!error;
      },
    },
  });

  return new ApolloClient({
    link: ApolloLink.from([authLink.concat(httpLink), retryLink]),
    cache: new InMemoryCache({
      typePolicies: {
        Zugang: { keyFields: ['uid'] },
      },
    }),
    defaultOptions: {
      watchQuery: {
        notifyOnNetworkStatusChange: false,
      },
    },
  });
}

export function E2nApiApolloProvider({ children }: { children: ReactNode }) {
  const { getAccessToken } = useE2nApiAuth();

  // Keep the latest getAccessToken without rebuilding the client on every render.
  const getAccessTokenRef = useRef(getAccessToken);
  getAccessTokenRef.current = getAccessToken;

  const [client] = useState(() =>
    buildClient(() => getAccessTokenRef.current()),
  );

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
}
